/* The customer picker and the two non-paint adders (catalogue products, custom lines). */
import { useEffect, useRef, useState } from 'preact/hooks';
import { searchCustomers, createCustomer, searchProducts } from '../lib/admin.js';
import { money, uid } from '../lib/quote.js';
import { toast } from '../lib/server.js';

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function CustomerPicker({ onPick, autoFocus }) {
  const [text, setText] = useState('');
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ firstName: '', lastName: '', email: '', phone: '', company: '' });
  const q = useDebounced(text, 250);
  const ref = useRef(null);

  useEffect(() => { if (autoFocus) setTimeout(() => ref.current?.focus?.(), 50); }, []);
  useEffect(() => {
    if (!q.trim()) { setRows(null); return; }
    let live = true;
    searchCustomers(q).then((r) => live && setRows(r)).catch((e) => toast(e.message, true));
    return () => { live = false; };
  }, [q]);

  const startNew = () => {
    const t = text.trim();
    const guess = { firstName: '', lastName: '', email: '', phone: '', company: '' };
    if (/@/.test(t)) guess.email = t;
    else if (/^[\d\s()+-]{7,}$/.test(t)) guess.phone = t;
    else if (t) {
      const [first, ...rest] = t.split(/\s+/);
      guess.firstName = first;
      guess.lastName = rest.join(' ');
    }
    setF(guess);
    setCreating(true);
  };

  const create = async () => {
    if (!f.email.trim() && !f.phone.trim()) return toast('Add an email or a phone number', true);
    setBusy(true);
    try {
      const c = await createCustomer({ ...f, email: f.email.trim(), phone: f.phone.trim() });
      toast(`${c.displayName} added to customers`);
      onPick(c);
    } catch (e) {
      toast(e.message, true);
    }
    setBusy(false);
  };

  if (creating) {
    return (
      <s-stack gap="base">
        <s-grid gridTemplateColumns="1fr 1fr" gap="base">
          <s-text-field label="First name" value={f.firstName} onInput={(e) => setF({ ...f, firstName: e.currentTarget.value })} />
          <s-text-field label="Last name" value={f.lastName} onInput={(e) => setF({ ...f, lastName: e.currentTarget.value })} />
          <s-email-field label="Email" value={f.email} onInput={(e) => setF({ ...f, email: e.currentTarget.value })} />
          <s-text-field label="Phone" value={f.phone} onInput={(e) => setF({ ...f, phone: e.currentTarget.value })} />
        </s-grid>
        <s-text-field label="Company / shop (optional)" value={f.company} onInput={(e) => setF({ ...f, company: e.currentTarget.value })} />
        <s-stack direction="inline" gap="base">
          <s-button variant="primary" loading={busy} onClick={create}>Add customer</s-button>
          <s-button variant="tertiary" onClick={() => setCreating(false)}>Cancel</s-button>
        </s-stack>
      </s-stack>
    );
  }

  return (
    <s-stack gap="small-200">
      <s-search-field
        ref={ref}
        label="Customer"
        labelAccessibilityVisibility="exclusive"
        placeholder="Search customers by name, email, phone or company"
        value={text}
        onInput={(e) => setText(e.currentTarget.value)}
      />
      {rows && rows.length > 0 && (
        <s-stack gap="none">
          {rows.map((c) => (
            <s-clickable key={c.id} onClick={() => onPick(c)} padding="small-200" borderRadius="base">
              <s-stack direction="inline" gap="base" alignItems="center">
                <s-avatar initials={(c.displayName || '?').slice(0, 2)} alt={c.displayName} size="small" />
                <s-stack gap="none">
                  <s-text type="strong">{c.displayName}</s-text>
                  <s-text color="subdued">{[c.email, c.phone, c.company || c.place].filter(Boolean).join(' · ')}</s-text>
                </s-stack>
              </s-stack>
            </s-clickable>
          ))}
        </s-stack>
      )}
      {rows && rows.length === 0 && <s-text color="subdued">No customer matches “{q}”.</s-text>}
      <s-stack direction="inline">
        <s-button variant="tertiary" icon="plus" onClick={startNew}>
          {text.trim() && rows?.length === 0 ? `Create “${text.trim()}”` : 'New customer'}
        </s-button>
      </s-stack>
    </s-stack>
  );
}

export function ProductAdder({ onAdd, onClose }) {
  const [text, setText] = useState('');
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const q = useDebounced(text, 300);

  useEffect(() => {
    if (q.trim().length < 2) { setRows(null); return; }
    let live = true;
    setLoading(true);
    searchProducts(q)
      .then((r) => live && setRows(r))
      .catch((e) => toast(e.message, true))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [q]);

  const addVariant = (v) => {
    onAdd([{ kind: 'product', variantId: v.variantId, title: v.title, variantTitle: v.variantTitle, sku: v.sku, quantity: 1, listPrice: v.price, unitPrice: v.price, attributes: [], image: v.image }]);
    toast(`Added ${v.title}`);
  };

  const browse = async () => {
    try {
      const picked = await shopify.resourcePicker({ type: 'product', action: 'add', multiple: true, filter: { variants: true, archived: false } });
      if (!picked) return;
      const lines = [];
      for (const p of picked) {
        for (const v of p.variants || []) {
          lines.push({
            kind: 'product', variantId: v.id, title: p.title,
            variantTitle: v.title && v.title !== 'Default Title' ? v.title : '',
            sku: v.sku || '', quantity: 1, listPrice: Number(v.price), unitPrice: Number(v.price), attributes: [],
            image: v.image?.originalSrc || p.images?.[0]?.originalSrc || '',
          });
        }
      }
      if (lines.length) onAdd(lines);
    } catch (e) {
      toast(e.message || 'The product picker did not open', true);
    }
  };

  return (
    <s-box padding="base" background="subdued" borderRadius="base" border="base">
      <s-stack gap="base">
        <s-stack direction="inline" justifyContent="space-between" alignItems="center">
          <s-heading>Add products</s-heading>
          <s-button variant="tertiary" icon="x" accessibilityLabel="Close" onClick={onClose} />
        </s-stack>
        <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="end">
          <s-search-field label="Search products" placeholder="Name, SKU or brand — e.g. 3M 06396, clearcoat, DA sander" value={text} onInput={(e) => setText(e.currentTarget.value)} />
          <s-button onClick={browse}>Browse catalog</s-button>
        </s-grid>
        {loading && <s-spinner accessibilityLabel="Searching" />}
        {rows && rows.length === 0 && !loading && <s-text color="subdued">Nothing matches “{q}”.</s-text>}
        {rows && rows.length > 0 && (
          <div class="results">
            <s-stack gap="none">
              {rows.map((v) => (
                <s-clickable key={v.variantId} onClick={() => addVariant(v)} padding="small-200" borderRadius="base">
                  <s-grid gridTemplateColumns="auto 1fr auto" gap="base" alignItems="center">
                    {v.image ? <img class="thumb" src={v.image} alt="" /> : <span class="thumb" />}
                    <s-stack gap="none">
                      <s-text type="strong">{v.title}</s-text>
                      <s-text color="subdued">
                        {[v.variantTitle, v.sku && `SKU ${v.sku}`, typeof v.stock === 'number' ? `${v.stock} in stock` : '', v.draft ? 'Draft product' : ''].filter(Boolean).join(' · ')}
                      </s-text>
                    </s-stack>
                    <s-text type="strong">{money(v.price)}</s-text>
                  </s-grid>
                </s-clickable>
              ))}
            </s-stack>
          </div>
        )}
      </s-stack>
    </s-box>
  );
}

export function CustomAdder({ onAdd, onClose }) {
  const [f, setF] = useState({ title: '', price: '', qty: '1', taxable: true, shipping: true });
  const add = () => {
    const price = parseFloat(f.price);
    if (!f.title.trim()) return toast('Name the item', true);
    if (!(price >= 0)) return toast('Enter a price', true);
    onAdd([{ uid: uid(), kind: 'custom', title: f.title.trim(), quantity: Math.max(1, parseInt(f.qty, 10) || 1), listPrice: price, unitPrice: price, taxable: f.taxable, requiresShipping: f.shipping, attributes: [] }]);
    setF({ title: '', price: '', qty: '1', taxable: true, shipping: true });
  };
  return (
    <s-box padding="base" background="subdued" borderRadius="base" border="base">
      <s-stack gap="base">
        <s-stack direction="inline" justifyContent="space-between" alignItems="center">
          <s-heading>Custom item</s-heading>
          <s-button variant="tertiary" icon="x" accessibilityLabel="Close" onClick={onClose} />
        </s-stack>
        <s-grid gridTemplateColumns="1fr 140px 100px" gap="base">
          <s-text-field label="Item" placeholder="e.g. Color match from sample, rush mixing" value={f.title} onInput={(e) => setF({ ...f, title: e.currentTarget.value })} />
          <s-money-field label="Price" value={f.price} onInput={(e) => setF({ ...f, price: e.currentTarget.value })} />
          <s-number-field label="Qty" min={1} value={f.qty} onInput={(e) => setF({ ...f, qty: e.currentTarget.value })} />
        </s-grid>
        <s-stack direction="inline" gap="large" alignItems="center">
          <s-checkbox label="Taxable" checked={f.taxable} onChange={(e) => setF({ ...f, taxable: e.currentTarget.checked })} />
          <s-checkbox label="Ships" checked={f.shipping} onChange={(e) => setF({ ...f, shipping: e.currentTarget.checked })} />
          <s-button variant="primary" onClick={add}>Add to quote</s-button>
        </s-stack>
      </s-stack>
    </s-box>
  );
}
